/* eslint-disable react/require-default-props */

// eslint-disable-next-line
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Constants, TextUtilities } from '@pwc/pwc-deals-web-common';
import { cloneDeep, debounce } from 'lodash';
import { DragDropContext, DragUpdate } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import { PayloadAction } from '@reduxjs/toolkit';
import { Loader } from '@pwc/pwc-deals-ui';
import { projectsContentsSearch } from 'features/analyses/analysesService';
import { DefaultCustomUnGroupedName } from 'features/analysesGroups/models/constants';
import {
  restrictedErrorMessage,
  isTitleRestricted,
} from 'common/helpers/titlesBlackList';
import featureFlags from 'common/helpers/featureFlags';
import {
  getErrorMessageDuplicateTitle,
  MatchItemsType,
  TITLE_DUPLICATION_CONTROL_FAILED,
} from 'common/helpers/duplicateTitleError';
import AutoLock from 'common/components/AutoLock';
import { getLockingUsers, LockingUser } from 'common/services/lockService';
import ModalLockingError from 'common/components/ModalLockingError/ModalLockingError';
import {
  deleteSingleAnalysis,
  deleteSingleGroup,
  getSelectedIds,
  moveCustomAnalysisGroup,
  renameGroup,
  clearGroupsSelection,
  moveAnalyses,
  deleteMultipleAnalyses,
  updateSortOrder,
} from '../../utils/groupsUtilities';
import {
  determineDragSubType,
  determineDragType,
  updateMultiDragBetweenGroups,
  updateMultiDragInsideOneGroup,
  updateSingleDragBetweenGroups,
  updateSingleDragInsideOneGroup,
  saveSingleAnalyses,
  saveMultipleAnalyses,
} from '../../utils/dragAndDropUtilities';
import AnalysesCustomList from '../AnalysesCustomList/AnalysesCustomList';
import MoveAnalysisModal from '../MoveAnalysisModal/MoveAnalysisModal';
import {
  DragType,
  DragSubType,
  GroupMoveDirection,
} from '../../models/constants';

import CurrentlyMovingAnalysis from '../../models/CurrentlyMovingAnalysis';
import DragUpdateGenerated from '../../models/DragUpdateGenerated';
import Group from '../../models/Group';
import GroupedAnalysis from '../../models/GroupedAnalysis';
import AnalysesHoverMenu from '../AnalysesHoverMenu/AnalysesHoverMenu';
import DragSource from '../../models/DragSource';
import GroupError from '../../models/GroupError';
import './AnalysesCustomGroupList.scss';
import { showModalError } from '../../../../common/redux/errors/errorsSlice';
import GroupsPermissions from '../GroupsPermissions/GroupsPermissions';
import GroupsModal, { GroupsModalProps } from '../GroupsModal';
import EmptyMessage from '../EmptyMessage';
import GroupPermissionsType from '../../models/GroupPermissionsType';

type Props = {
  groups: Group[];
  analysisCount: number;
  setAnalysisCount: (analysisCount: number) => void;
  setGroups: (groupData: Group[]) => void;
  tags?: number[] | string[];
  isAddingNewGroup: boolean;
  groupsLoading: boolean | undefined;
  permissions: GroupPermissionsType;
  enableDragAndDrop: boolean;
  displaySortingHandler: (groups: Group[]) => void;
};

const DEFAULT_ERRORS = {
  hasDeleteError: false,
  hasRenameError: false,
};

const AnalysesCustomGroupList: React.FC<Props> = ({
  groups = [],
  setGroups,
  analysisCount = undefined,
  setAnalysisCount,
  tags,
  isAddingNewGroup = false,
  groupsLoading = false,
  permissions,
  enableDragAndDrop = true,
  displaySortingHandler,
}) => {
  // eslint-disable-next-line
  const dispatch = useDispatch();
  const [sourceId, setSourceId] = useState<string>();
  // eslint-disable-next-line
  const { projectId } = useParams<{ projectId: string }>();
  const [selectedIDs, setSelectedIDs] = useState<number[]>([]);
  const [dragging, setDragging] = useState<boolean>(false);
  const [modalMoveAnaly, setModalMoveAnaly] = useState<boolean>(false);
  const [groupError, setGroupError] = useState<GroupError>(DEFAULT_ERRORS);
  const [groupErrorMessage, setGroupErrorMessage] = useState<string>('');
  const increment = isAddingNewGroup ? 1 : 0;
  const [currMoveAnaly, setCurrMoveAnaly] =
    useState<CurrentlyMovingAnalysis | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const { t } = useTranslation();
  const [modal, setModal] = useState<GroupsModalProps>({
    show: false,
  });

  const [analysisToBeDeletedId, setAnalysisToBeDeletedId] =
    useState<number>(-1);
  const lockingUserRef = useRef('');

  // setInterval Id which checks if there is any user locking the page. It's used to clean the interval once checking the locking user is not needed
  const intervalIdRef = useRef<NodeJS.Timer | null>();
  const [showModalLockingError, setShowModalLockingError] =
    useState<boolean>(false);

  const enableAutoSave = featureFlags.isAutoSaveEnabled;

  const displayErrorModal = (modalErorr: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    dispatch(showModalError(modalErorr) as PayloadAction);
  };

  const displayOrderErrorMessage = () => {
    displayErrorModal(Constants.ModalErrorType.UNABLE_TO_SAVE_SORT_ORDER);
  };

  const displayChangeErrorMessage = () => {
    displayErrorModal(Constants.ModalErrorType.UNABLE_TO_SAVE_CHANGE);
  };

  const deselectAll = (groupsToDeselect?: Group[] | undefined) => {
    if (groupsToDeselect) {
      const updatedGroups = clearGroupsSelection(groupsToDeselect);
      setGroups(updatedGroups);
      setSelectedIDs([]);
      return;
    }

    clearGroupsSelection(groups);
    setSelectedIDs([]);
  };

  const handleDeleteGroup = async (groupId: number) => {
    setBusy(true);
    deselectAll();
    const updatedGroups = await deleteSingleGroup(projectId, groupId);
    if (updatedGroups) {
      const groupsModified = (updatedGroups || []).map((grp: Group) => ({
        ...grp,
        items: [],
      }));
      setGroups(groupsModified);
      displaySortingHandler(groupsModified);
    } else {
      setGroupError({ hasDeleteError: true });
      displayChangeErrorMessage();
    }
    setBusy(false);
  };

  const handleDeleteAnalysis = async (analysisId: number) => {
    setBusy(true);
    const updatedGroups = await deleteSingleAnalysis(
      projectId,
      groups,
      analysisId
    );
    if (updatedGroups) {
      setGroups(updatedGroups);
      if (analysisCount) {
        setAnalysisCount(analysisCount - 1);
        setSelectedIDs(selectedIDs.filter((id: number) => id !== analysisId));
      }
    } else {
      displayChangeErrorMessage();
    }
    setBusy(false);
  };

  const handleDeleteAnalyses = async () => {
    setBusy(true);
    deselectAll();
    const updatedGroups = await deleteMultipleAnalyses(
      projectId,
      groups,
      selectedIDs
    );
    if (updatedGroups) {
      setGroups(updatedGroups);
      if (analysisCount) {
        setAnalysisCount(analysisCount - selectedIDs.length);
        setSelectedIDs([]);
      }
    } else {
      displayChangeErrorMessage();
    }
    setBusy(false);
  };

  const showDeleteGroupModal = (id: number) => {
    setModal({
      modalActionType: 'GroupsDelete',
      show: true,
      onCancel: () => {
        setModal({ show: false });
      },
      onConfirm: async () => {
        setModal({ show: false });
        await handleDeleteGroup(id);
      },
    });
  };

  useEffect(() => {
    if (modal && modal.show && analysisToBeDeletedId !== -1) {
      const checkLockingUser = async () => {
        const lockingUser = (await getLockingUsers(projectId, [
          analysisToBeDeletedId,
        ])) as LockingUser;

        if (lockingUser.isLocked) {
          lockingUserRef.current = lockingUser.name;
        } else {
          lockingUserRef.current = '';
        }
      };

      intervalIdRef.current = setInterval(checkLockingUser, 2000);
    } else {
      setAnalysisToBeDeletedId(-1);
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    }
  }, [modal, analysisToBeDeletedId, projectId]);

  const showDeleteAnalysisModal = (id: number) => {
    setModal({
      modalActionType: 'AnalysesDelete',
      show: true,
      onCancel: () => {
        setModal({ show: false });
      },
      onConfirm: async () => {
        setModal({ show: false });
        try {
          if (lockingUserRef.current) {
            setShowModalLockingError(true);
            return;
          }
          await handleDeleteAnalysis(id);
        } catch (e) {
          setGroupError({ hasDeleteError: true });
          displayChangeErrorMessage();
        }
      },
      additionalClass: 'analysis-delete-modal',
    });

    setAnalysisToBeDeletedId(id);
  };

  const showDeleteAnalysesModal = () => {
    setModal({
      modalActionType: 'AnalysesDelete',
      show: true,
      onCancel: () => {
        setModal({ show: false });
      },
      onConfirm: async () => {
        setModal({ show: false });
        await handleDeleteAnalyses();
      },
    });
  };

  const showMoveAnalysisModal = (
    contentThreadId: number,
    sortOrder: number,
    moveFromGroupID: number
  ) => {
    setCurrMoveAnaly({
      contentThreadId,
      sortOrder,
      moveFromGroupID,
    });
    setModalMoveAnaly(true);
  };

  const searchTitleDuplicity = useCallback(
    async (newGroupName: string, groupId: number) => {
      const trimmedTitle = TextUtilities.trimText(newGroupName) as string;

      if (!trimmedTitle.length) {
        setGroupError({ hasRenameError: false });
        setGroupErrorMessage('');
        return false;
      }

      if (
        trimmedTitle.toLowerCase() ===
        TextUtilities.trimText(DefaultCustomUnGroupedName.toLowerCase())
      ) {
        setGroupError({ id: groupId, hasRenameError: true });
        setGroupErrorMessage(
          getErrorMessageDuplicateTitle(
            {
              contentType: 900,
              contentId: 0,
              contentSubType: 0,
              contentThreadId: 0,
              title: '',
              versionStatus: 0,
            },
            t
          )
        );
        return false;
      }
      if (isTitleRestricted(newGroupName)) {
        setGroupError({ id: groupId, hasRenameError: true });
        setGroupErrorMessage(restrictedErrorMessage);
        return false;
      }
      try {
        const matchItems = (await projectsContentsSearch(projectId, {
          title: trimmedTitle,
        })) as MatchItemsType;
        if (
          matchItems &&
          matchItems.length > 0 &&
          (
            TextUtilities.trimText(matchItems[0]?.title || '') as string
          ).toLowerCase() === trimmedTitle.toLowerCase() &&
          matchItems[0].contentId !== groupId
        ) {
          setGroupError({ id: groupId, hasRenameError: true });
          setGroupErrorMessage(getErrorMessageDuplicateTitle(matchItems[0], t));
          return false;
        }
        if (groupError?.hasRenameError) {
          setGroupError({ hasRenameError: false });
          setGroupErrorMessage('');
        }
      } catch (e) {
        setGroupError({ id: groupId, hasRenameError: true });
        setGroupErrorMessage(t(TITLE_DUPLICATION_CONTROL_FAILED));
        return false;
      }

      return true;
    },
    [groupError, projectId, t]
  );

  const debouncedSearchTitleDuplicity = useMemo(
    () => debounce(searchTitleDuplicity, 250),
    [searchTitleDuplicity]
  );

  const handleRenameGroup = async (newName: string, groupId: number) => {
    setBusy(true);
    if (!groups || !groups.length) return;
    const isUniqueTitle = await searchTitleDuplicity(newName, groupId);
    if (!isUniqueTitle) {
      setBusy(false);
      return;
    }
    const updatedGroups = await renameGroup(
      projectId,
      groups,
      newName,
      groupId
    );
    if (updatedGroups) {
      setGroups(updatedGroups);
    } else {
      setGroupErrorMessage(
        'Unable to edit the group name. Please refresh the page to try again.'
      );
      setGroupError({ id: groupId, hasRenameError: true });
    }
    setBusy(false);
  };

  const handleAnalysesMove = async (group: Group) => {
    setBusy(true);
    deselectAll();
    const updatedGroups = await moveAnalyses(
      projectId,
      groups || [],
      group.title,
      selectedIDs
    );
    if (updatedGroups) {
      setGroups(updatedGroups);
    } else {
      displayOrderErrorMessage();
    }
    setBusy(false);
  };

  const handleGroupMove = async (
    groupId: number,
    direction: GroupMoveDirection
  ) => {
    setBusy(true);
    const updatedGroups = await moveCustomAnalysisGroup(
      groups,
      groupId,
      direction,
      projectId
    );
    if (updatedGroups) {
      setGroups(updatedGroups);
    } else {
      displayOrderErrorMessage();
    }
    setBusy(false);
  };

  const handleSelectItem = (contentThreadId: number, selected: boolean) => {
    const { updatedGroups, updatedSelectedIDs } = getSelectedIds(
      groups,
      contentThreadId,
      selected
    );
    setGroups(updatedGroups);
    setSelectedIDs(updatedSelectedIDs);
  };

  const handleDeselect = () => {
    deselectAll();
  };

  const clearGroupError = () => {
    setGroupError(DEFAULT_ERRORS);
  };

  const handleBeforeDragStart = (ev: DragSource) => {
    const { source } = ev;
    const { droppableId } = source;
    setSourceId(droppableId);
  };

  const handleDragStart = () => {
    setDragging(true);
  };

  const handleDragEnd = async (ev: DragUpdate | DragUpdateGenerated) => {
    if (dragging) {
      setDragging(false);
    }
    const { destination } = ev;

    if (!destination) return;

    const dragType: DragType = determineDragType(ev);
    const dragSubType: DragSubType = determineDragSubType(
      selectedIDs,
      ev,
      groups
    );

    const oldGroups = cloneDeep(groups);
    if (dragType === 'insideOneGroup') {
      if (dragSubType === 'singledrag') {
        const {
          updatedGroups,
          destGroupId,
          destinationIndex,
          contentThreadIds,
        } = updateSingleDragInsideOneGroup(groups, ev);
        setGroups(updatedGroups);
        const success = await saveSingleAnalyses(
          projectId,
          oldGroups,
          contentThreadIds as number[],
          destGroupId,
          destinationIndex
        );
        if (!success) {
          displayOrderErrorMessage();
          setGroups(groups);
        } else {
          deselectAll(updatedGroups);
        }
      }

      if (dragSubType === 'multidrag') {
        const { updatedGroups, destGroupId, destinationIndex } =
          updateMultiDragInsideOneGroup(groups, ev);
        setGroups(updatedGroups);
        const success = await saveMultipleAnalyses(
          projectId,
          oldGroups,
          selectedIDs,
          destGroupId,
          destinationIndex
        );
        if (!success) {
          setGroups(groups);
          displayOrderErrorMessage();
        } else {
          deselectAll(updatedGroups);
        }
      }
    }

    if (dragType === 'fromGroupToGroup') {
      if (dragSubType === 'singledrag') {
        const {
          updatedGroups,
          destGroupId,
          destinationIndex,
          contentThreadIds,
        } = updateSingleDragBetweenGroups(groups, ev);
        setGroups(updatedGroups);
        const success = await saveSingleAnalyses(
          projectId,
          oldGroups,
          contentThreadIds as number[],
          destGroupId,
          destinationIndex
        );
        if (!success) {
          displayOrderErrorMessage();
          setGroups(groups);
        } else {
          deselectAll(updatedGroups);
        }
      }

      if (dragSubType === 'multidrag') {
        const { updatedGroups, destGroupId, destinationIndex } =
          updateMultiDragBetweenGroups(oldGroups, ev, selectedIDs);
        setGroups(updatedGroups);
        const success = await saveMultipleAnalyses(
          projectId,
          oldGroups,
          selectedIDs,
          destGroupId,
          destinationIndex
        );
        if (!success) {
          setGroups(groups);
          displayOrderErrorMessage();
        } else {
          deselectAll(updatedGroups);
        }
      }
    }
  };

  const handleMoveDown = (groupId: number) => handleGroupMove(groupId, 1);
  const handleMoveUp = (groupId: number) => handleGroupMove(groupId, 0);

  const handleMoveAnalysis = async (group: Group) => {
    const { contentThreadId } = currMoveAnaly as CurrentlyMovingAnalysis;
    setBusy(true);
    deselectAll();
    const updatedGroups = await moveAnalyses(
      projectId,
      groups || [],
      group.title,
      [contentThreadId]
    );
    if (updatedGroups) {
      setGroups(updatedGroups);
    } else {
      displayOrderErrorMessage();
    }
    setBusy(false);
    setCurrMoveAnaly(null);
    setModalMoveAnaly(false);
  };

  const setItemsForGroup = (groupId: number, items: GroupedAnalysis[]) => {
    const clonedGroups = cloneDeep(groups);
    for (let i = 0; i < clonedGroups.length; i += 1) {
      if (clonedGroups[i].groupId === groupId) {
        clonedGroups[i].items = items;
      }
    }
    updateSortOrder(clonedGroups);
    setGroups(clonedGroups);
  };

  if (groupsLoading || busy) {
    return <Loader />;
  }

  const ids = groups.reduce((acc: number[], group) => {
    group.items.forEach((item) => {
      acc.push(item.contentThreadId);
    });
    return acc;
  }, []);

  return (
    <>
      {!busy && (
        <div className="analyses-custom-view-container">
          {!groupsLoading && (
            <EmptyMessage
              groupType="custom"
              groupsCount={groups.length}
              analysisCount={analysisCount}
            />
          )}
          {modal.show && <GroupsModal {...modal} />}
          {showModalLockingError && (
            <ModalLockingError
              show={showModalLockingError}
              onModalClose={() => setShowModalLockingError(false)}
              actionName="delete"
              contentType="Analysis"
              userName={lockingUserRef.current}
            />
          )}
          {currMoveAnaly && (
            <MoveAnalysisModal
              show={modalMoveAnaly}
              setShow={setModalMoveAnaly}
              confirm={handleMoveAnalysis}
              groups={groups}
              itemToMove={currMoveAnaly}
            />
          )}

          <DragDropContext
            onBeforeDragStart={handleBeforeDragStart}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
          >
            <AutoLock
              projectId={Number(projectId)}
              contentThreadIds={ids}
              config={{ enabled: Boolean(enableAutoSave) }}
            >
              {groups.map((group, i) => (
                <AnalysesCustomList
                  key={`${group.title}-${group.groupId}`}
                  // eslint-disable-next-line
                  projectId={projectId}
                  group={group}
                  groupIndex={i + 1}
                  handleDeleteGroup={showDeleteGroupModal}
                  handleDeleteAnalysis={showDeleteAnalysisModal}
                  handleMoveAnalysis={showMoveAnalysisModal}
                  handleRename={handleRenameGroup}
                  handleSelect={handleSelectItem}
                  list={group.items}
                  setList={setItemsForGroup}
                  selectedIDs={selectedIDs}
                  number={i < groups.length - 1 ? i + 1 + increment : 0}
                  dragInProgress={dragging}
                  isFirstGroup={i === 0}
                  isLastGroup={i === groups.length - 2}
                  handleMoveUp={handleMoveUp}
                  handleMoveDown={handleMoveDown}
                  sourceId={sourceId}
                  tags={tags}
                  groupError={groupError}
                  clearGroupError={clearGroupError}
                  permissions={permissions}
                  enableDragAndDrop={enableDragAndDrop}
                  groupErrorMessage={groupErrorMessage}
                  searchTitleDuplicity={debouncedSearchTitleDuplicity}
                />
              ))}
            </AutoLock>
          </DragDropContext>

          <GroupsPermissions projectId={Number(projectId)}>
            <>
              {!dragging && selectedIDs.length > 1 && (
                <div className="analyses-custom-view-container__hover-menu">
                  <AnalysesHoverMenu
                    groups={groups || []}
                    selectedAnalysisCount={selectedIDs.length}
                    handleDeselect={handleDeselect}
                    handleDelete={showDeleteAnalysesModal}
                    handleMove={handleAnalysesMove}
                  />
                </div>
              )}
            </>
          </GroupsPermissions>
        </div>
      )}
    </>
  );
};

export default AnalysesCustomGroupList;
